import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  Divider,
  IconButton,
  Tooltip,
  Chip,
  Stack,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ReplyIcon from '@mui/icons-material/Reply';
import ForwardIcon from '@mui/icons-material/Forward';
import DeleteIcon from '@mui/icons-material/Delete';
import ArchiveIcon from '@mui/icons-material/Archive';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import LockIcon from '@mui/icons-material/Lock';
import VerifiedIcon from '@mui/icons-material/Verified';
import CodeIcon from '@mui/icons-material/Code';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import ForumIcon from '@mui/icons-material/Forum';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { format, parseISO } from 'date-fns';
import {
  getMessage,
  getMessageRaw,
  markStarred,
  trashMessage,
  archiveMessage,
  moveMessage,
  copyMessage,
} from '../api/mailbox';
import FilterRuleDialog from '../components/FilterRuleDialog';
import EmailHtmlFrame from '../components/EmailHtmlFrame';
import FolderTransferDialog from '../components/FolderTransferDialog';
import MessageContextMenu from '../components/MessageContextMenu';
import MessageAttachments from '../components/MessageAttachments';
import AddSenderContactButton from '../components/AddSenderContactButton';
import MessagePgpActions from '../components/MessagePgpActions';

interface MessagePageProps {
  embeddedMessageId?: number;
  hideBack?: boolean;
}

export default function MessagePage({ embeddedMessageId, hideBack }: MessagePageProps = {}) {
  const { messageId } = useParams<{ messageId: string }>();
  const id = embeddedMessageId || parseInt(messageId!, 10);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [viewHtml, setViewHtml] = useState(true);
  const [rawOpen, setRawOpen] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [transferMode, setTransferMode] = useState<'move' | 'copy' | null>(null);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(null);

  const { data: msg, isLoading, isError } = useQuery({
    queryKey: ['message', id],
    queryFn: () => getMessage(id),
    enabled: !!id,
  });

  // Standalone /message/:id → always open the conversation view when threaded
  useEffect(() => {
    if (embeddedMessageId) return;
    if (msg?.thread) {
      navigate(`/thread/${msg.thread}?msg=${msg.id}`, { replace: true });
    }
  }, [embeddedMessageId, msg, navigate]);

  const { data: rawData, isFetching: rawLoading } = useQuery({
    queryKey: ['message-raw', id],
    queryFn: () => getMessageRaw(id),
    enabled: rawOpen && !!id,
  });

  const starMutation = useMutation({
    mutationFn: (starred: boolean) => markStarred(id, starred),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['message', id] });
      queryClient.invalidateQueries({ queryKey: ['threads'] });
      queryClient.invalidateQueries({ queryKey: ['thread'] });
    },
  });

  const trashMutation = useMutation({
    mutationFn: () => trashMessage(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['messages'] });
      queryClient.invalidateQueries({ queryKey: ['threads'] });
      if (!embeddedMessageId) navigate(-1);
    },
  });

  const archiveMutation = useMutation({
    mutationFn: () => archiveMessage(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['messages'] });
      queryClient.invalidateQueries({ queryKey: ['threads'] });
      if (!embeddedMessageId) navigate(-1);
    },
  });

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (isError || !msg) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Failed to load message.
      </Alert>
    );
  }

  // Avoid flashing the flat view before redirect
  if (!embeddedMessageId && msg.thread) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const hasBody = Boolean(msg.body_html || msg.body_text);

  return (
    <Box sx={{ p: { xs: 1, md: 2 }, maxWidth: embeddedMessageId ? '100%' : 900, mx: 'auto' }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        {!hideBack && (
          <Tooltip title="Back">
            <IconButton onClick={() => navigate(-1)} aria-label="Back">
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="Reply">
          <IconButton onClick={() => navigate(`/compose?reply=${id}`)} aria-label="Reply">
            <ReplyIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Forward">
          <IconButton onClick={() => navigate(`/compose?forward=${id}`)} aria-label="Forward">
            <ForwardIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title={msg.is_starred ? 'Unstar' : 'Star'}>
          <IconButton
            onClick={() => starMutation.mutate(!msg.is_starred)}
            aria-label={msg.is_starred ? 'Unstar' : 'Star'}
          >
            {msg.is_starred ? <StarIcon color="warning" /> : <StarBorderIcon />}
          </IconButton>
        </Tooltip>
        <Tooltip title="Archive">
          <IconButton onClick={() => archiveMutation.mutate()} aria-label="Archive">
            <ArchiveIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete">
          <IconButton onClick={() => trashMutation.mutate()} aria-label="Delete">
            <DeleteIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="View raw source">
          <IconButton onClick={() => setRawOpen(true)} aria-label="View raw source">
            <CodeIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Move to folder or account">
          <IconButton onClick={() => setTransferMode('move')} aria-label="Move">
            <DriveFileMoveIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Copy to folder or account">
          <IconButton onClick={() => setTransferMode('copy')} aria-label="Copy">
            <ContentCopyIcon />
          </IconButton>
        </Tooltip>
        <Button
          size="small"
          variant="outlined"
          startIcon={<FilterAltIcon />}
          onClick={() => setFilterOpen(true)}
        >
          Filter
        </Button>
        {msg.thread && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<ForumIcon />}
            onClick={() => navigate(`/thread/${msg.thread}`)}
          >
            Thread
          </Button>
        )}
      </Stack>

      <Paper
        sx={{ p: { xs: 2, md: 3 } }}
        elevation={1}
        onContextMenu={(e) => {
          e.preventDefault();
          setMenuPos({ top: e.clientY, left: e.clientX });
        }}
      >
        <Typography variant="h6" gutterBottom fontWeight={600}>
          {msg.subject || '(no subject)'}
        </Typography>

        <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
          {msg.is_encrypted && (
            <Chip icon={<LockIcon />} label="Encrypted" color="info" size="small" />
          )}
          {msg.is_signed && (
            <Chip
              icon={<VerifiedIcon />}
              label={msg.gpg_verified ? 'Signature valid' : 'Signed (unverified)'}
              color={msg.gpg_verified ? 'success' : 'warning'}
              size="small"
            />
          )}
        </Stack>

        <Box sx={{ mb: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }} flexWrap="wrap" useFlexGap>
            <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
              <b>From:</b> {msg.from_address}
            </Typography>
            <AddSenderContactButton fromAddress={msg.from_address} />
            <Button
              size="small"
              color="primary"
              startIcon={<FilterAltIcon />}
              onClick={() => setFilterOpen(true)}
              sx={{ flexShrink: 0 }}
            >
              Filter from sender
            </Button>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            <b>To:</b> {msg.to_addresses}
          </Typography>
          {msg.cc_addresses && (
            <Typography variant="body2" color="text.secondary">
              <b>Cc:</b> {msg.cc_addresses}
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary">
            <b>Date:</b> {msg.date ? format(parseISO(msg.date), 'PPpp') : 'Unknown'}
          </Typography>
        </Box>

        <Divider sx={{ mb: 2 }} />

        <MessagePgpActions bodyText={msg.body_text} bodyHtml={msg.body_html} />

        {msg.body_html && msg.body_text && (
          <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
            <Button
              size="small"
              variant={viewHtml ? 'contained' : 'outlined'}
              onClick={() => setViewHtml(true)}
            >
              HTML
            </Button>
            <Button
              size="small"
              variant={!viewHtml ? 'contained' : 'outlined'}
              onClick={() => setViewHtml(false)}
            >
              Plain text
            </Button>
          </Stack>
        )}

        {!hasBody ? (
          <Alert severity="info">
            No message body was returned from the server. Try opening again, or view raw source.
          </Alert>
        ) : viewHtml && msg.body_html ? (
          <EmailHtmlFrame html={msg.body_html} title={msg.subject || 'Email'} />
        ) : (
          <Box
            component="pre"
            sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
          >
            {msg.body_text}
          </Box>
        )}

        {msg.attachments && msg.attachments.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <MessageAttachments attachments={msg.attachments} messageId={msg.id} />
          </>
        )}
      </Paper>

      <Dialog open={rawOpen} onClose={() => setRawOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>Raw email source</DialogTitle>
        <DialogContent>
          {rawLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box
              component="pre"
              sx={{
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                fontSize: 12,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                m: 0,
              }}
            >
              {rawData?.raw || '(empty)'}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRawOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <FilterRuleDialog open={filterOpen} onClose={() => setFilterOpen(false)} message={msg} />

      <FolderTransferDialog
        open={transferMode !== null}
        mode={transferMode || 'move'}
        excludeFolderId={msg.folder}
        onClose={() => setTransferMode(null)}
        onSelect={async (folderId) => {
          if (transferMode === 'copy') {
            await copyMessage(id, folderId);
          } else {
            await moveMessage(id, folderId);
            queryClient.invalidateQueries({ queryKey: ['threads'] });
            if (!embeddedMessageId) navigate(-1);
          }
          queryClient.invalidateQueries({ queryKey: ['folders'] });
          queryClient.invalidateQueries({ queryKey: ['messages'] });
        }}
      />

      <MessageContextMenu
        anchorPosition={menuPos}
        message={{
          id: msg.id,
          folder: msg.folder,
          from_address: msg.from_address,
          to_addresses: msg.to_addresses,
          cc_addresses: msg.cc_addresses,
          subject: msg.subject,
        }}
        onClose={() => setMenuPos(null)}
        afterLeave={() => {
          if (!embeddedMessageId) navigate(-1);
        }}
      />
    </Box>
  );
}
